import { useState, useEffect, useMemo } from "react";
import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Calendar, PoundSterling, MapPin, Clock, Users, Mail, Phone, Building, FileText, Plus, Edit, Repeat } from "lucide-react";
import { format } from "date-fns";
import type { ExternalEvent, Venue, Booking, ActWithParticipants } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { BookingFormModal } from "@/components/bookings/booking-form-modal";
import { ExternalEventFormModal } from "@/components/events/external-event-form-modal";
import CreateEventModal from "@/components/create-event-modal";

type ExternalEventWithVenue = ExternalEvent & { venue?: Venue };
type BookingWithAct = Booking & {
  act: ActWithParticipants;
  externalEvent: ExternalEventWithVenue;
};

export default function EventSummary() {
  const { orgId, eventId } = useParams();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("time-asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [prePopulatedEventData, setPrePopulatedEventData] = useState<any>(null);

  // Check permissions
  useEffect(() => {
    if (!orgId || !eventId) {
      setLocation("/");
      return;
    }

    const organisationId = parseInt(orgId || "0");
    const hasAccess = user?.role === 'god' || 
      user?.organisationUsers?.some(ou => 
        ou.organisation.id === organisationId && 
        (ou.role === 'owner' || ou.role === 'operator')
      );

    if (!hasAccess) {
      setLocation("/");
    }
  }, [orgId, eventId, user]); // Removed setLocation from dependencies

  // Fetch event details
  const { data: event, isLoading: isLoadingEvent } = useQuery<ExternalEventWithVenue>({
    queryKey: [`/api/external-events/${eventId}`],
    queryFn: async () => {
      const response = await fetch(`/api/external-events/${eventId}`);
      if (!response.ok) throw new Error("Failed to fetch event");
      return response.json();
    },
    enabled: !!eventId,
  });

  // Fetch bookings for this event
  const { data: bookings = [], isLoading: isLoadingBookings } = useQuery<BookingWithAct[]>({
    queryKey: [`/api/bookings?organisationId=${orgId}&externalEventId=${eventId}`],
    queryFn: async () => {
      const response = await fetch(`/api/bookings?organisationId=${orgId}&externalEventId=${eventId}`);
      if (!response.ok) throw new Error("Failed to fetch bookings");
      return response.json();
    },
    enabled: !!eventId && !!orgId,
  });

  // All hooks must be called before any conditional returns
  // Determine contact info
  const contactName = useMemo(() => 
    event?.useVenueContact ? event?.venue?.contactName : event?.contactName,
    [event?.useVenueContact, event?.venue?.contactName, event?.contactName]
  );
  const contactEmail = useMemo(() => 
    event?.useVenueContact ? event?.venue?.contactEmail : event?.contactEmail,
    [event?.useVenueContact, event?.venue?.contactEmail, event?.contactEmail]
  );
  const contactPhone = useMemo(() => 
    event?.useVenueContact ? event?.venue?.contactPhone : event?.contactPhone,
    [event?.useVenueContact, event?.venue?.contactPhone, event?.contactPhone]
  );

  // Filter and sort bookings
  const filteredBookings = useMemo(() => {
    return bookings
      .filter(booking => {
        // Status filter
        if (statusFilter !== "all" && booking.status !== statusFilter) return false;
        
        // Search filter
        if (searchQuery) {
          const search = searchQuery.toLowerCase();
          const actName = booking.act?.name || booking.act?.participants?.[0]?.participant.name || "";
          return (
            actName.toLowerCase().includes(search) ||
            booking.notes?.toLowerCase().includes(search)
          );
        }
        
        return true;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case "time-asc":
            if (!a.performanceTime && !b.performanceTime) return 0;
            if (!a.performanceTime) return 1;
            if (!b.performanceTime) return -1;
            return new Date(a.performanceTime).getTime() - new Date(b.performanceTime).getTime();
          case "time-desc":
            if (!a.performanceTime && !b.performanceTime) return 0;
            if (!a.performanceTime) return -1;
            if (!b.performanceTime) return 1;
            return new Date(b.performanceTime).getTime() - new Date(a.performanceTime).getTime();
          case "fee-asc":
            return (a.fee || 0) - (b.fee || 0);
          case "fee-desc":
            return (b.fee || 0) - (a.fee || 0);
          case "status":
            return (a.status || "").localeCompare(b.status || "");
          case "act":
            const aName = a.act?.name || a.act?.participants?.[0]?.participant.name || "";
            const bName = b.act?.name || b.act?.participants?.[0]?.participant.name || "";
            return aName.localeCompare(bName);
          default:
            return 0;
        }
      });
  }, [bookings, statusFilter, sortBy, searchQuery]);

  const calculateTotalCost = useMemo(() => {
    return filteredBookings
      .filter(b => b.fee)
      .reduce((sum, b) => sum + (b.fee || 0), 0) / 100;
  }, [filteredBookings]);

  const calculateTotalCommission = useMemo(() => {
    return filteredBookings
      .filter(b => b.commissionAmount)
      .reduce((sum, b) => sum + (b.commissionAmount || 0) / 100, 0);
  }, [filteredBookings]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 border-green-200";
      case "tentative":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "completed":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getBookingStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "completed":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (isLoadingEvent || isLoadingBookings) {
    return (
      <PageBackground>
        <AppHeader />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <Calendar className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700" />
            <p className="text-slate-700">Loading event details...</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (!event) {
    return (
      <PageBackground>
        <AppHeader />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <p className="text-slate-700">Event not found</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <AppHeader backUrl={`/organisations/${orgId}/acts`} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">

          <div className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-slate-900 drop-shadow-sm flex items-center gap-3">
                  <Calendar className="h-8 w-8 text-blue-600" />
                  {event.name}
                </h1>
                <div className="mt-3 space-y-2">
                  {event.venue && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building className="h-4 w-4" />
                      <span className="font-medium">{event.venue.name}</span>
                    </div>
                  )}
                  {event.venue?.address && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="h-4 w-4" />
                      <span>
                        {event.venue.address}
                        {event.venue.city && `, ${event.venue.city}`}
                        {event.venue.state && `, ${event.venue.state}`}
                        {event.venue.zipCode && ` ${event.venue.zipCode}`}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="h-4 w-4" />
                    <span>{format(new Date(event.date), "PPP 'at' p")}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => setShowEditEventModal(true)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Event
                </Button>
                <Button
                  onClick={() => {
                    const eventDate = new Date(event.date);
                    const dateTimeString = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}T${String(eventDate.getHours()).padStart(2, '0')}:${String(eventDate.getMinutes()).padStart(2, '0')}`;
                    
                    setPrePopulatedEventData({
                      name: event.name,
                      type: "jam", // Default to jam, user can change in modal
                      date: dateTimeString,
                      venue: event.venue?.name || "",
                      description: event.notes || "",
                      externalEventId: event.id,
                    });
                    setShowCreateEventModal(true);
                  }}
                  size="sm"
                  variant="outline"
                  className="bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700"
                >
                  <Repeat className="h-4 w-4 mr-2" />
                  Convert to Managed Event
                </Button>
                <Badge className={`${getStatusColor(event.status)} px-3 py-1`}>
                  {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                </Badge>
                {event.budget && (
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Budget</p>
                    <p className="text-lg font-semibold">
                      £{(event.budget / 100).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Info */}
            {(contactName || contactEmail || contactPhone) && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-sm font-medium text-slate-700 mb-2">Event Contact</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  {contactName && (
                    <span className="flex items-center gap-1 text-slate-600">
                      <Users className="h-3 w-3" />
                      {contactName}
                    </span>
                  )}
                  {contactEmail && (
                    <a
                      href={`mailto:${contactEmail}`}
                      className="flex items-center gap-1 text-blue-600 hover:underline"
                    >
                      <Mail className="h-3 w-3" />
                      {contactEmail}
                    </a>
                  )}
                  {contactPhone && (
                    <a
                      href={`tel:${contactPhone}`}
                      className="flex items-center gap-1 text-blue-600 hover:underline"
                    >
                      <Phone className="h-3 w-3" />
                      {contactPhone}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
              <div className="bg-white/30 rounded-lg p-3">
                <p className="text-xs text-slate-600">Total Acts</p>
                <p className="text-xl font-bold">{bookings.length}</p>
              </div>
              <div className="bg-white/30 rounded-lg p-3">
                <p className="text-xs text-slate-600">Confirmed</p>
                <p className="text-xl font-bold text-green-600">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
              </div>
              <div className="bg-white/30 rounded-lg p-3">
                <p className="text-xs text-slate-600">Total Cost</p>
                <p className="text-xl font-bold">£{calculateTotalCost.toLocaleString()}</p>
              </div>
              <div className="bg-white/30 rounded-lg p-3">
                <p className="text-xs text-slate-600">Commission</p>
                <p className="text-xl font-bold">£{calculateTotalCommission.toFixed(2)}</p>
              </div>
              {event.budget && (
                <div className="bg-white/30 rounded-lg p-3">
                  <p className="text-xs text-slate-600">Remaining</p>
                  <p className={`text-xl font-bold ${
                    (event.budget / 100) - calculateTotalCost < 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    £{((event.budget / 100) - calculateTotalCost).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {event.notes && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-sm text-slate-600 italic">{event.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="mb-6 bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search acts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white/50"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px] bg-white/50">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[180px] bg-white/50">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="time-asc">Performance Time</SelectItem>
                  <SelectItem value="time-desc">Time (Reverse)</SelectItem>
                  <SelectItem value="act">Act Name</SelectItem>
                  <SelectItem value="fee-desc">Fee (Highest)</SelectItem>
                  <SelectItem value="fee-asc">Fee (Lowest)</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => setShowBookingModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Book Act
              </Button>
            </div>
          </div>
        </div>

        {/* Bookings List */}
        <div className="space-y-4">
          {filteredBookings.length === 0 ? (
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  No bookings found
                </h3>
                <p className="text-slate-600">
                  {statusFilter !== "all" || searchQuery 
                    ? "Try adjusting your filters"
                    : "No acts have been booked for this event yet"}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredBookings.map((booking) => {
              const actName = booking.act?.name || booking.act?.participants?.[0]?.participant.name || "Unknown Act";
              
              return (
                <Card 
                  key={booking.id} 
                  className="bg-white/20 backdrop-blur-xl border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer"
                  onClick={() => setLocation(`/organisations/${orgId}/bookings/${booking.id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-lg font-semibold text-slate-900">
                            {actName}
                          </h3>
                          <Badge className={getBookingStatusColor(booking.status)}>
                            {booking.status}
                          </Badge>
                        </div>
                        
                        {booking.act?.participants && booking.act.participants.length > 0 && booking.act.name && (
                          <div className="mb-2 flex flex-wrap gap-1">
                            {booking.act.participants.map(ap => (
                              <Badge key={ap.participantId} variant="outline" className="text-xs">
                                {ap.participant.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        <div className="space-y-1 text-sm text-slate-600">
                          {booking.performanceTime && (
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              <span>
                                {format(new Date(booking.performanceTime), "p")}
                                {booking.durationMinutes && ` (${booking.durationMinutes} min)`}
                              </span>
                            </div>
                          )}
                          {booking.act?.genre && booking.act.genre.length > 0 && (
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4" />
                              <span>{booking.act.genre.join(", ")}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {booking.fee && (
                        <div className="text-right">
                          <div className="flex items-center gap-2 justify-end mb-1">
                            <PoundSterling className="h-4 w-4 text-slate-600" />
                            <span className="text-lg font-semibold">
                              £{(booking.fee / 100).toFixed(2)}
                            </span>
                          </div>
                          {booking.commissionAmount && (
                            <p className="text-xs text-slate-600">
                              Commission: {booking.commissionType === 'percentage' && booking.commissionValue
                                ? `${(booking.commissionValue / 100).toFixed(1)}%`
                                : 'Flat'
                              }
                              {' '}
                              (£{(booking.commissionAmount / 100).toFixed(2)})
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {booking.notes && (
                      <p className="mt-3 text-sm text-slate-600 italic">
                        {booking.notes}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </main>

      {/* Booking Form Modal */}
      <BookingFormModal
        open={showBookingModal}
        onOpenChange={setShowBookingModal}
        organisationId={parseInt(orgId || "0")}
        eventId={parseInt(eventId || "0")}
      />

      {/* Edit Event Modal */}
      <ExternalEventFormModal
        open={showEditEventModal}
        onOpenChange={setShowEditEventModal}
        organisationId={parseInt(orgId || "0")}
        event={event}
      />

      {/* Create Managed Event Modal */}
      <CreateEventModal
        isOpen={showCreateEventModal}
        onOpenChange={setShowCreateEventModal}
        organisationId={parseInt(orgId || "0")}
        prePopulatedData={prePopulatedEventData}
        onSuccess={(eventId) => {
          // Navigate to the new event
          setLocation(`/event/${eventId}`);
        }}
      />
    </PageBackground>
  );
}